import { platformCustomViewEditPermission } from '@agilelab/plugin-wb-rbac-common';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  Box,
  IconButton,
  makeStyles,
  MenuList,
  Paper,
  Popover,
} from '@material-ui/core';
import GetApp from '@material-ui/icons/GetApp';
import MoreVert from '@material-ui/icons/MoreVert';
import { MouseEvent, ReactElement, useState } from 'react';
import { dumpJsxAsYaml, saveYaml } from '../../utils';
import { CustomViewDownloadItem } from './CustomViewDownloadItem';
import { CustomViewSubMenu } from './CustomViewSubMenu';
import { CustomViewPages } from './pages';
import { extractIncludeFromCode } from './utils';

const useStyles = makeStyles(theme => ({
  standardPositioned: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  onlyIcon: {
    minWidth: 0,
    borderRadius: '50%',
    padding: '4px',
  },
  option: {
    padding: '8px',
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.bkg.primary,
    },
  },
}));

const SegmentDownloader = ({
  children,
  filename,
  done,
}: {
  children: ReactElement;
  filename: string;
  done: Function;
}) => {
  if (children) {
    saveYaml(filename, dumpJsxAsYaml(children));
    done();
  }
  return <></>;
};

export const CustomViewDownloader = ({
  customViewIds,
  withMenu,
}: {
  customViewIds: string[];
  withMenu?: boolean;
}) => {
  const [dumpSegment, setDumpSegment] = useState<{ fn: Function | undefined }>({
    fn: undefined,
  });
  const [dumpFileName, setDumpFileName] = useState<string>('');
  const { allowed: canEdit } = usePermission({
    permission: platformCustomViewEditPermission,
  });
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement>();
  const open = Boolean(anchorEl);
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    if (anchorEl) setAnchorEl(undefined);
    else setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(undefined);
  };

  const classes = useStyles();
  const customViewChildren = customViewIds?.map(customViewId => {
    const getCustomViewChildren = CustomViewPages?.get(customViewId);
    return getCustomViewChildren!();
  });
  const included = customViewChildren?.flatMap(child =>
    extractIncludeFromCode(child as ReactElement, '', ''),
  );
  const menu = customViewIds
    ?.map(id => {
      const getCustomViewChildren = CustomViewPages?.get(id);
      const child = getCustomViewChildren!();
      return {
        id: id,
        label: CustomViewPages.getMetadata(id)?.label ?? id,
        action: () => {
          saveYaml(id, dumpJsxAsYaml(child));
        },
      };
    })
    .concat(
      included?.map((inc: any) => ({
        id: inc.id,
        label: CustomViewPages.getMetadata(inc.id)?.label ?? inc.id,
        action: () => {
          setDumpFileName(inc.id);
          setDumpSegment({
            fn: CustomViewPages.get(inc.id),
          });
        },
      })),
    );

  if (!canEdit) return <></>;
  const subMenu = (
    <>
      <CustomViewSubMenu
        icon={<GetApp fontSize="small" />}
        text="Custom View Download"
      >
        <MenuList>
          <Paper style={{ borderRadius: 0, background: 'white' }}>
            {menu?.map((el, i) => (
              <CustomViewDownloadItem key={i} item={el} included={included} />
            ))}
          </Paper>
        </MenuList>
      </CustomViewSubMenu>
      <SegmentDownloader
        filename={dumpFileName}
        done={() => setDumpSegment({ fn: undefined })}
      >
        {dumpSegment.fn ? dumpSegment.fn() : undefined}
      </SegmentDownloader>
    </>
  );
  if (!withMenu) return subMenu;

  return (
    <Box className={classes.standardPositioned}>
      <Box onClick={onClick} onKeyDown={() => true}>
        <IconButton size="small" aria-label="more">
          <MoreVert />
        </IconButton>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          {subMenu}
        </Box>
      </Popover>
    </Box>
  );
};
